import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { AnalyticsProvider } from '../../contexts/AnalyticsContext';
import { CreditAnalytics } from '../credit/CreditAnalytics';
import { MultiRoundDisputeManager } from '../disputes/MultiRoundDisputeManager';
import { TradelinesManager } from '../tradelines/TradelinesManager';
import { DocumentManager } from '../documents/DocumentManager';
import { UserProfile } from '../user/UserProfile';
import { UserPasswordAuth } from '../user/UserPasswordAuth';
import { ReferralV1 } from '../referral/ReferralV1';
import { apiService, ApiError } from '../../services/api';
import { useConvexRealtimeStatus } from '../../contexts/ConvexRealtimeProvider';
import {
  User,
  LogOut,
  BarChart3,
  FileText,
  TrendingUp,
  Menu,
  X,
  Upload,
  Mail,
  Phone,
  Repeat,
  AlertTriangle,
  Shield,
  PanelLeftClose,
  PieChart,
  File,
  Files,
  CreditCard,
  LineChart,
  CheckCircle,
  XCircle,
  IdCard,
  Calendar,
  Eye,
  EyeOff,
  Loader2,
  KeyRound,
  Fuel,
  Link2,
  Check,
  ExternalLink
} from 'lucide-react';

interface ConvexErrorBoundaryState {
  hasError: boolean;
  message: string;
}

class ConvexErrorBoundary extends React.Component<{ children: React.ReactNode }, ConvexErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: Error): ConvexErrorBoundaryState {
    const message = error?.message || 'Failed to load tradelines.';
    return { hasError: true, message };
  }

  render() {
    if (this.state.hasError) {
      const isMissingUserIdClaim = this.state.message.includes('missing userId claim');
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm font-medium text-red-700 mb-1">Unable to load realtime tradelines</p>
          <p className="text-sm text-red-600">
            {isMissingUserIdClaim
              ? 'Convex token is missing userId claim. Update /api/v1/auth/convex-token to include userId in token claims.'
              : this.state.message}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

export function Layout() {
  const { user, manager, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const validTabs = ['analytics', 'multiround', 'tradelines', 'documents', 'profile', 'password-auth', 'referral-v1'];
  const tabParam = searchParams.get('tab');
  const initialTab = tabParam && validTabs.includes(tabParam) ? tabParam : 'analytics';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(new Set(['analytics', initialTab]));

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [version, setVersion] = useState<string>('');
  const [showSSN, setShowSSN] = useState(false);
  const [fullSSN, setFullSSN] = useState<string | null>(null);
  const [loadingSSN, setLoadingSSN] = useState(false);
  const [copiedUserLink, setCopiedUserLink] = useState(false);
  const { isConnected: isConvexConnected } = useConvexRealtimeStatus();

  useEffect(() => {
    setVisitedTabs(prev => {
      if (prev.has(activeTab)) return prev;
      return new Set(prev).add(activeTab);
    });
  }, [activeTab]);

  useEffect(() => {
    // Fetch version from version.txt
    fetch('/version.txt')
      .then(response => response.text())
      .then(text => setVersion(text.trim()))
      .catch(err => console.error('Failed to load version:', err));
  }, []);

  const toggleSSNVisibility = async () => {
    // If hiding, just toggle off
    if (showSSN) {
      setShowSSN(false);
      return;
    }

    // If showing and we don't have the full SSN yet, fetch it
    if (!fullSSN && user?.id) {
      setLoadingSSN(true);
      try {
        const response = await apiService.getUserSSN(user.id);
        console.log('SSN Response:', response);
        if (response.success && response.data.ssn) {
          console.log('Full SSN received:', response.data.ssn);
          setFullSSN(response.data.ssn);
          setShowSSN(true);
        }
      } catch (error) {
        console.error('Failed to fetch SSN:', error);
      } finally {
        setLoadingSSN(false);
      }
    } else {
      // We already have the SSN, just show it
      console.log('Using cached SSN:', fullSSN);
      setShowSSN(true);
    }
  };

  const formatSSN = (ssnLast4: string | null | undefined, isVisible: boolean) => {
    if (!isVisible) {
      return '•••-••-••••';
    }
    
    if (fullSSN) {
      // Remove any non-digit characters
      const digitsOnly = fullSSN.replace(/\D/g, '');
      
      // Format full SSN as XXX-XX-XXXX
      if (digitsOnly.length === 9) {
        return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 5)}-${digitsOnly.slice(5)}`;
      }
      // If it already has formatting, return as-is
      if (fullSSN.includes('-')) {
        return fullSSN;
      }
      return fullSSN;
    }
    
    return '•••-••-••••';
  };

  // Check if manager is admin
  const managerSession = localStorage.getItem('managerSession');
  const managerData = managerSession ? JSON.parse(managerSession) : null;
  const managerRole = managerData?.role || 'manager';
  const isAdmin = managerRole === 'admin';

  const handleExit = () => {
    logout();
    // If manager/admin is viewing as a user, navigate to admin console
    if (managerSession) {
      navigate('/admin');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCopyUserDirectLink = async () => {
    if (!user?.id) return;

    const directLink = `${window.location.origin}/users/${user.id}?tab=${activeTab}`;

    try {
      await navigator.clipboard.writeText(directLink);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = directLink;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }

    setCopiedUserLink(true);
    window.setTimeout(() => setCopiedUserLink(false), 2000);
  };

  const navigationItems = [
    { id: 'analytics', label: 'Analytics', icon: PieChart },
    { id: 'multiround', label: 'Disputes', icon: File },
    { id: 'tradelines', label: 'Tradelines', icon: LineChart },
    { id: 'documents', label: 'Documents', icon: IdCard },
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'password-auth', label: 'Password', icon: KeyRound },
    { id: 'referral-v1', label: 'Referral v1', icon: Fuel },
  ];

  const getSubscriptionLabel = (subscriptionType?: string) => {
    if (!subscriptionType) return 'Free';
    if (subscriptionType.startsWith('FULL_ACCESS')) return 'Max';
    if (subscriptionType.startsWith('PRO_ACCESS')) return 'Pro';
    if (subscriptionType === 'FREE') return 'Free';
    return 'Free';
  };

  const planLabel = getSubscriptionLabel(user?.subscriptionType);
  const isGodModeEnabled = Boolean(
    user?.maxPlanEnabled ||
      (user as { max_plan_mode?: boolean } | null)?.max_plan_mode
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Sidebar - Fixed position, doesn't scroll with content */}
      <aside className={`${isSidebarOpen ? 'w-[218px]' : 'w-16'} border-r bg-card transition-all duration-300 flex flex-col fixed left-0 top-0 h-screen z-10`}>
        {/* Logo Section */}
        <div className="h-[88px] px-4 flex items-center justify-between flex-shrink-0">
          {isSidebarOpen && (
            <div className="flex items-center gap-2">
              <img
                src="/logo.png"
                alt="CreditBooster.ai"
                className="h-7 w-7"
              />
              <span className="text-base font-semibold text-foreground">CreditBooster.ai</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="h-8 w-8 p-0 ml-10"
          >
            {isSidebarOpen ? <PanelLeftClose className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </div>

        {/* Navigation - Scrollable if needed */}
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => handleTabChange(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm font-medium ${
                  activeTab === item.id
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {isSidebarOpen && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Admin Console Link - At bottom of sidebar */}
        {isAdmin && (
          <div className="border-t flex-shrink-0">
            <a
              href="/admin"
              className="w-full flex items-center justify-center gap-2 py-3 transition-colors hover:bg-gray-50 text-gray-900 text-sm font-medium border-b"
            >
              <Shield className="h-4 w-4 flex-shrink-0" />
              {isSidebarOpen && <span>Admin Console</span>}
            </a>
            
            {/* Manager Info */}
            {isSidebarOpen && manager && (
              <div className="px-3 py-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {manager.firstName} {manager.lastName}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {managerRole === 'admin' ? 'Admin' : 'Manager'}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="h-8 w-8 p-0 ml-2"
                    title="Logout"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </aside>

      {/* Spacer to prevent content from going under fixed sidebar */}
      <div className={`${isSidebarOpen ? 'w-[218px]' : 'w-16'} flex-shrink-0 transition-all duration-300`} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Max plan mode banner */}
        {isGodModeEnabled && (
          <div className="bg-green-100 border-b border-green-300 px-6 py-1.5">
            <p className="text-sm font-semibold text-green-900">
              Wow, this user has God mode enabled.
            </p>
          </div>
        )}

        {/* Top Bar - User Info when manager is viewing as user */}
        {user && managerSession && (
          <header className="bg-white px-6 py-4">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 w-full">
                <div className="flex items-center gap-1 min-w-[150px]">
                  <p className="text-base font-semibold text-gray-900">
                    {user.firstName} {user.lastName}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyUserDirectLink}
                    className="h-7 w-7 p-0"
                    title="Copy direct link to this user"
                    aria-label="Copy direct link to this user"
                  >
                    {copiedUserLink ? (
                      <Check className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <Link2 className="h-3.5 w-3.5 text-gray-600" />
                    )}
                  </Button>
                </div>
                <div className="bg-white border border-gray-300 rounded-lg px-2 py-0.5 flex items-center justify-center">
                  <span className="text-xs font-semibold text-gray-900 leading-4 whitespace-nowrap">{planLabel}</span>
                </div>
                {user.isTrial && (
                  <div className="flex items-center gap-1 px-2.5 py-0.5 rounded-lg border border-blue-300 bg-blue-50">
                    <span className="text-xs font-medium text-blue-700">Trial</span>
                    {user.trialExpiresAt && (
                      <span className="text-xs text-blue-500">· ends {new Date(user.trialExpiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    )}
                  </div>
                )}
                <div className="flex items-center">
                  {user.enrolled ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg border border-gray-300 bg-white">
                      <CheckCircle className="h-3.5 w-3.5 text-gray-900" />
                      <span className="text-xs font-medium text-gray-900">Enrolled</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg border border-gray-300 bg-white">
                      <XCircle className="h-3.5 w-3.5 text-gray-500" />
                      <span className="text-xs font-medium text-gray-500">Not Enrolled</span>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 h-5">
                  <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-900 whitespace-nowrap">{user.phoneNumber}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-900 whitespace-nowrap">{user.email || 'john.smith@email.com'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-900 font-mono whitespace-nowrap">
                    {formatSSN(user.ssnLast4, showSSN)}
                  </span>
                  <button
                    onClick={toggleSSNVisibility}
                    className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                    title={showSSN ? 'Hide SSN' : 'Show SSN'}
                    disabled={loadingSSN}
                  >
                    {loadingSSN ? (
                      <Loader2 className="h-4 w-4 text-gray-600 animate-spin" />
                    ) : showSSN ? (
                      <EyeOff className="h-4 w-4 text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-600" />
                    )}
                  </button>
                </div>
                <div className="flex items-center gap-2 h-5">
                  <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-900 whitespace-nowrap">Member Since: {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '/') : '15/11/2025'}</span>
                </div>
                {user.crsUserId && (
                  <a
                    href={`https://wgt.stitchcredit.com/customer/user/${user.crsUserId}/logs`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors whitespace-nowrap"
                  >
                    <ExternalLink className="h-3.5 w-3.5 text-blue-600" />
                    <span className="text-xs font-medium text-blue-700">CRS Console</span>
                  </a>
                )}
              </div>
            </div>
          </header>
        )}

        {/* Deleted User Banner - Only shown when manager is viewing a deleted user */}
        {manager && user?.deleted && (
          <div className="bg-destructive/15 border-b border-destructive/30 px-6 py-3">
            <div className="flex items-center space-x-2 text-destructive">
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-sm">Deleted User Account</p>
                <p className="text-xs opacity-90">
                  This user account has been deleted. The account is view-only and cannot perform regular operations.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Content Area */}
        <main className="flex-1 overflow-auto px-6 pt-3 pb-6">
          <AnalyticsProvider>
            {/* Keep visited tabs mounted but hidden to preserve state and avoid re-fetching */}
            <div style={{ display: activeTab === 'analytics' ? 'block' : 'none' }}>
              <CreditAnalytics isActive={activeTab === 'analytics'} />
            </div>
            {visitedTabs.has('multiround') && (
              <div style={{ display: activeTab === 'multiround' ? 'block' : 'none' }}>
                <MultiRoundDisputeManager isActive={activeTab === 'multiround'} />
              </div>
            )}
            {visitedTabs.has('tradelines') && (
              <div style={{ display: activeTab === 'tradelines' ? 'block' : 'none' }}>
                <ConvexErrorBoundary>
                  <TradelinesManager />
                </ConvexErrorBoundary>
              </div>
            )}
            {visitedTabs.has('documents') && (
              <div style={{ display: activeTab === 'documents' ? 'block' : 'none' }}>
                <DocumentManager isActive={activeTab === 'documents'} />
              </div>
            )}
            {visitedTabs.has('profile') && (
              <div style={{ display: activeTab === 'profile' ? 'block' : 'none' }}>
                <UserProfile isActive={activeTab === 'profile'} />
              </div>
            )}
            {visitedTabs.has('password-auth') && (
              <div style={{ display: activeTab === 'password-auth' ? 'block' : 'none' }}>
                <UserPasswordAuth />
              </div>
            )}
            {visitedTabs.has('referral-v1') && (
              <div style={{ display: activeTab === 'referral-v1' ? 'block' : 'none' }}>
                <ReferralV1 isActive={activeTab === 'referral-v1'} />
              </div>
            )}
          </AnalyticsProvider>
        </main>

        {/* Footer */}
        <footer className="bg-card px-6 py-2">
          <div className="flex items-center justify-center gap-1.5">
            {version && (
              <span className="text-xs text-gray-400">v{version}</span>
            )}
            <span
              className={`inline-block h-2 w-2 rounded-full ${isConvexConnected ? 'bg-green-500' : 'bg-red-500'}`}
              title={isConvexConnected ? 'Realtime connected' : 'Realtime disconnected'}
            />
          </div>
        </footer>
      </div>
    </div>
  );
}
